import React, { useEffect, useRef, useState } from 'react';
import { Modal } from '../../../../components/Modal';
import { Camera, RefreshCw } from 'lucide-react';

type Props = {
    clientId: string;
    onClose: () => void;
    onCaptured: (file: File) => void;
};

export function CameraCapture({ clientId, onClose, onCaptured }: Props) {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const streamRef = useRef<MediaStream | null>(null);

    // evita setState após unmount / após uma tentativa antiga
    const mountedRef = useRef(true);
    const startSeqRef = useRef(0);

    const [starting, setStarting] = useState(false);
    const [started, setStarted] = useState(false);
    const [error, setError] = useState<string>('');
    const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
    const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
    const [shouldResume, setShouldResume] = useState(false);

    const formatErr = (err: any) => `${err?.name || 'Error'}: ${err?.message || 'Falha ao acessar câmera'}`;

    const stopStream = () => {
        // limpa o elemento de vídeo também (ajuda no iOS/PWA)
        const v = videoRef.current;
        if (v) {
            try {
                v.pause();
            } catch {}
            // @ts-ignore
            v.srcObject = null;
            v.removeAttribute('src');
            try {
                v.load();
            } catch {}
        }

        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
    };

    const detectMultipleCameras = async () => {
        try {
            // enumerateDevices pode falhar se não estiver em contexto seguro ou sem permissão
            const devices = await navigator.mediaDevices.enumerateDevices();
            const cams = devices.filter((d) => d.kind === 'videoinput').length;
            setHasMultipleCameras(cams > 1);
        } catch {
            // ignore
        }
    };

    // ✅ evita travar esperando loadedmetadata (muito comum em iOS PWA)
    const waitVideoReady = (v: HTMLVideoElement, ms = 1500) =>
        new Promise<void>((resolve, reject) => {
            let done = false;

            const cleanup = () => {
                v.removeEventListener('loadedmetadata', onReady);
                v.removeEventListener('loadeddata', onReady);
                v.removeEventListener('canplay', onReady);
                v.removeEventListener('playing', onReady);
                clearTimeout(timer);
            };

            const onReady = () => {
                if (done) return;
                done = true;
                cleanup();
                resolve();
            };

            // já tem dimensões? resolve imediatamente
            if (v.readyState >= 2 && v.videoWidth > 0 && v.videoHeight > 0) {
                resolve();
                return;
            }

            const timer = window.setTimeout(() => {
                cleanup();
                reject(new Error('VIDEO_READY_TIMEOUT'));
            }, ms);

            v.addEventListener('loadedmetadata', onReady, { once: true });
            v.addEventListener('loadeddata', onReady, { once: true });
            v.addEventListener('canplay', onReady, { once: true });
            v.addEventListener('playing', onReady, { once: true });
        });

    const attachStreamToVideo = async (stream: MediaStream) => {
        const v = videoRef.current;
        if (!v) return;

        // @ts-ignore
        v.srcObject = stream;
        v.muted = true;
        v.playsInline = true;
        v.autoplay = true;

        // tenta dar play (pode falhar sem gesto do usuário)
        try {
            await v.play();
        } catch {
            // ok: o usuário pode tocar no vídeo para liberar
        }

        // não pode depender só de loadedmetadata (pode nunca disparar no PWA iOS)
        await waitVideoReady(v, 1500);
    };

    const startStream = async (mode: 'user' | 'environment') => {
        const seq = ++startSeqRef.current;

        if (!navigator.mediaDevices?.getUserMedia) {
            setError('Navegador não suporta câmera (getUserMedia).');
            return;
        }

        if (!window.isSecureContext) {
            setError('A câmera exige HTTPS (contexto seguro).');
            return;
        }

        // evita tentar iniciar se o app está em background
        if (document.visibilityState !== 'visible') return;

        setStarting(true);
        setError('');

        try {
            stopStream();

            // 1) tenta com constraints
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: { ideal: mode },
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                },
                audio: false,
            });

            // se já iniciou outra tentativa depois, aborta esta
            if (!mountedRef.current || seq !== startSeqRef.current) {
                stream.getTracks().forEach((t) => t.stop());
                return;
            }

            streamRef.current = stream;

            try {
                await attachStreamToVideo(stream);
                if (!mountedRef.current || seq !== startSeqRef.current) return;

                await detectMultipleCameras();
                setStarted(true);
            } catch (e) {
                // se o vídeo não ficou pronto a tempo, não “trava”; deixa o usuário tocar para liberar
                if (!mountedRef.current || seq !== startSeqRef.current) return;

                setStarted(false);
                setError(
                    'A prévia da câmera não iniciou automaticamente. Toque no vídeo para ativar ou tente novamente.'
                );
            }
        } catch (e: any) {
            // 2) fallback sem constraints
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });

                if (!mountedRef.current || seq !== startSeqRef.current) {
                    stream.getTracks().forEach((t) => t.stop());
                    return;
                }

                streamRef.current = stream;

                try {
                    await attachStreamToVideo(stream);
                    if (!mountedRef.current || seq !== startSeqRef.current) return;

                    await detectMultipleCameras();
                    setStarted(true);
                } catch {
                    if (!mountedRef.current || seq !== startSeqRef.current) return;

                    setStarted(false);
                    setError(
                        'A prévia da câmera não iniciou automaticamente. Toque no vídeo para ativar ou tente novamente.'
                    );
                }
            } catch (e2: any) {
                setError(formatErr(e2 || e));
                setStarted(false);
            }
        } finally {
            if (mountedRef.current && seq === startSeqRef.current) {
                setStarting(false);
            }
        }
    };

    // ✅ Auto-inicia ao abrir (montar)
    useEffect(() => {
        mountedRef.current = true;

        startStream(facingMode);

        return () => {
            mountedRef.current = false;
            stopStream();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ✅ PWA: se app ficar em background, para stream; quando volta, retoma
    useEffect(() => {
        const onVis = () => {
            if (document.hidden) {
                if (started || starting) setShouldResume(true);
                stopStream();
                setStarted(false);
            } else {
                if (shouldResume) {
                    setShouldResume(false);
                    startStream(facingMode);
                }
            }
        };

        document.addEventListener('visibilitychange', onVis);
        return () => document.removeEventListener('visibilitychange', onVis);
    }, [started, starting, shouldResume, facingMode]);

    const handleClose = () => {
        stopStream();
        onClose();
    };

    const toggleCamera = async () => {
        const next = facingMode === 'environment' ? 'user' : 'environment';
        setFacingMode(next);
        await startStream(next);
    };

    const capture = async () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) return;

        const w = video.videoWidth || 0;
        const h = video.videoHeight || 0;

        if (!w || !h) {
            setError('Câmera ainda não está pronta. Tente novamente.');
            return;
        }

        canvas.width = w;
        canvas.height = h;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.setTransform(1, 0, 0, 1, 0, 0);

        if (facingMode === 'user') {
            ctx.translate(w, 0);
            ctx.scale(-1, 1);
        }

        ctx.drawImage(video, 0, 0, w, h);

        const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
        if (!blob) return;

        const file = new File([blob], `cliente-${clientId}-${Date.now()}.jpg`, { type: 'image/jpeg' });
        onCaptured(file);
        handleClose();
    };

    return (
        <Modal onClose={handleClose} title="Tirar foto do cliente" subTitle="A câmera abre automaticamente">
            <div className="space-y-3">
                {error && (
                    <div className="alert alert-warning text-sm">
                        {error}
                        <div className="text-xs opacity-70 mt-2">
                            Dica: em iPhone/PWA, às vezes precisa tocar no vídeo para liberar o preview.
                        </div>
                    </div>
                )}

                <div
                    className="relative rounded-box overflow-hidden border border-base-300 bg-black"
                    onClick={() => {
                        // se o play foi bloqueado, toque aqui geralmente libera
                        if (!started && !starting) startStream(facingMode);
                        videoRef.current?.play?.().catch(() => {});
                    }}
                >
                    <div className="w-full h-[60vh] sm:h-[360px]">
                        <video
                            ref={videoRef}
                            className={['w-full h-full object-cover', facingMode === 'user' ? '-scale-x-100' : ''].join(' ')}
                            playsInline
                            muted
                            autoPlay
                        />
                    </div>

                    <div className="absolute top-2 left-2 flex gap-2">
                        <span className="badge badge-ghost text-xs">{facingMode === 'environment' ? 'Traseira' : 'Frontal'}</span>
                        {starting && <span className="badge badge-ghost text-xs">Iniciando...</span>}
                    </div>
                </div>

                <canvas ref={canvasRef} className="hidden" />

                <div className="sticky bottom-0 bg-base-100 pt-1 pb-[env(safe-area-inset-bottom)]">
                    <div className="flex flex-col sm:flex-row gap-2 sm:justify-between">
                        <button className="btn btn-ghost w-full sm:w-auto" onClick={handleClose} type="button">
                            Cancelar
                        </button>

                        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                            {hasMultipleCameras && (
                                <button
                                    className="btn btn-outline w-full sm:w-auto"
                                    onClick={toggleCamera}
                                    disabled={starting}
                                    type="button"
                                >
                                    <RefreshCw className="mr-2" aria-hidden="true" />
                                    Trocar câmera
                                </button>
                            )}

                            <button
                                className="btn btn-primary w-full sm:w-auto"
                                onClick={capture}
                                disabled={!started || !!error || starting}
                                type="button"
                            >
                                {starting ? (
                                    <span className="loading loading-spinner loading-xs mr-2" />
                                ) : (
                                    <Camera className="mr-2" aria-hidden="true" />
                                )}
                                Capturar
                            </button>
                        </div>
                    </div>

                    {!started && !error && (
                        <div className="text-xs opacity-70 mt-2">Se a prévia não aparecer, toque no vídeo para ativar.</div>
                    )}
                </div>
            </div>
        </Modal>
    );
}
